// Persistent slim footer banner shown on every public route.
// Sits above the AudioPlayer when the player is visible. Dismissable per
// session. Renders either an AdSense in-page ad or the Sponsor Card fallback
// via <AdSlot slotKey="footer" />. Hidden on admin routes and if there's
// nothing to show.
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import { useAdsEligibility, validSlot } from '@/lib/adsEligibility';
import AdSlot from '@/components/AdSlot';

const DISMISS_KEY = 'christovibes:footer_ad_dismissed:v1';

export default function FooterAdBar() {
  const { pathname } = useLocation();
  const { currentTrack, radioMode } = usePlayer();
  const { settings, enabled, regionBlocked, consent } = useAdsEligibility();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    // Re-hide on route change if it was dismissed (dismissal is per-session anyway)
    try { setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1'); } catch { /* noop */ }
  }, [pathname]);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  if (pathname.startsWith('/admin')) return null;
  if (dismissed) return null;
  if (!consent) return null;

  const footerSlot = settings?.ad_slots?.footer || '';
  const canShowAd = enabled && validSlot(footerSlot);
  const canShowSponsor = !!(regionBlocked && settings?.sponsor_card?.enabled);
  if (!canShowAd && !canShowSponsor) return null;

  const playerVisible = !!currentTrack || radioMode;

  return (
    <div
      data-testid="footer-ad-bar"
      className="fixed left-0 right-0 z-40 pointer-events-none"
      style={{ bottom: playerVisible ? 72 : 0 }}
    >
      <div className="mx-auto max-w-5xl px-3 pb-3 sm:pb-4">
        <div className="pointer-events-auto relative rounded-2xl border border-white/10 bg-[#0F1115]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden">
          <button
            data-testid="footer-ad-dismiss"
            onClick={dismiss}
            className="absolute top-2 right-2 z-10 p-1.5 rounded-full text-[#64748B] hover:text-white hover:bg-white/5"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
          <div className="px-4 sm:px-5 py-2 sm:py-3">
            <AdSlot slotKey="footer" page={pathname} label="" className="!my-0" />
          </div>
        </div>
      </div>
    </div>
  );
}
